import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ZoomIn } from "lucide-react";
import { Reveal, Eyebrow, LineReveal } from "../components/motion";
import BookingCta from "../components/BookingCta";
import BookingSearch from "../components/BookingSearch";
import ReviewsWall from "../components/ReviewsWall";
import Lightbox from "../components/Lightbox";
import { IMAGES, DOMES } from "../config/site";

function FantailBird() {
  return (
    <svg viewBox="0 0 52 44" className="h-10 w-12 md:h-12 md:w-14 shrink-0 -mt-2" role="img" aria-label="Pīwakawaka (fantail)" data-testid="piwakawaka-bird-mark">
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        <g stroke="#C4B5FD" strokeWidth="1.7">
          <path d="M30 24 L41 12" />
          <path d="M30 24 L45.5 16.5" />
          <path d="M30 24 L47 23" />
          <path d="M30 24 L44.5 30.5" />
          <path d="M30 24 L39.5 36.5" />
          <path d="M41 12 A15.5 15.5 0 0 1 39.5 36.5" opacity="0.65" />
        </g>
        <path
          d="M29.5 24 Q28 20 24 19 Q20 18 17.5 20 Q13.5 18.5 12 21 L7.5 22.5 L12 24.5 Q11.5 29.5 16 32 Q20.5 34.5 25 32 Q29 30 30 25"
          stroke="#A78BFA"
          strokeWidth="2"
        />
        <path d="M20 35 V39 M23.5 34.5 V39" stroke="#A78BFA" strokeWidth="1.5" />
      </g>
      <circle cx="15.5" cy="22" r="1.2" fill="#F9F6F0" />
    </svg>
  );
}

function FalconBird() {
  return (
    <svg viewBox="0 0 52 48" className="h-11 w-12 md:h-13 md:w-14 shrink-0 -mt-2" role="img" aria-label="Kārearea — New Zealand falcon" data-testid="karearea-bird-mark">
      {/* perched falcon silhouette: hooked beak, fierce brow, long wing and tail */}
      <path
        d="M5.6 11.2 Q9.6 8.6 14.2 8.4 Q20.4 8.4 24.2 12.6 Q27.6 16.6 28.4 22 Q29.2 27.6 30.8 33.2 Q32.2 38.6 32.8 44.2 L28.8 42.4 Q27.8 40.4 26.8 38.8 L30 46.4 L23.8 44.6 Q22.2 40.8 22 37.6 Q16.8 36.2 13.6 31.6 Q10.4 27 10.2 21.4 Q10 16.8 8.2 14.6 Q6.6 13.2 5.6 11.2 Z"
        fill="#C4B5FD"
      />
      {/* folded wing feather line */}
      <path d="M21.5 14.8 Q26 20.8 26.6 30.6" fill="none" stroke="#0B120E" strokeWidth="1.3" opacity="0.35" strokeLinecap="round" />
      {/* tail band */}
      <path d="M25.6 41.6 L28.8 43.4" fill="none" stroke="#0B120E" strokeWidth="1.2" opacity="0.35" strokeLinecap="round" />
      {/* fierce eye + dark malar stripe */}
      <circle cx="12.4" cy="14.4" r="1.3" fill="#0B120E" />
      <path d="M12.7 16.2 Q13.7 19 12.5 21.8 Q11.3 18.6 12.7 16.2 Z" fill="#0B120E" opacity="0.85" />
      {/* talons */}
      <g fill="none" stroke="#C4B5FD" strokeWidth="1.4" strokeLinecap="round">
        <path d="M17.5 39 V43" />
        <path d="M21 38.6 V43" />
        <path d="M15.8 43.3 H19.2 M19.4 43.3 H23" />
      </g>
    </svg>
  );
}

function ZoomHint() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-ink/35 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
      <ZoomIn size={26} className="text-cream" />
    </div>
  );
}

function GalleryBlock({ dome }) {
  const [index, setIndex] = useState(null);
  // owner request: drop gallery pictures 1 and 3 (g01, g03), then the top-middle photo (g04)
  const gallery = dome.gallery.filter((_, i) => i !== 0 && i !== 2 && i !== 3);
  const photos = [dome.img, ...gallery];
  const nav = (d) => setIndex((i) => (i + d + photos.length) % photos.length);
  return (
    <>
      <Reveal y={40} delay={0.1} className="mt-14">
        <div
          className="img-frame aspect-[4/3] cursor-pointer group"
          onClick={() => setIndex(0)}
          data-testid={`dome-cover-${dome.id}`}
        >
          <img src={dome.img} alt={`${dome.fullName} — geodesic dome at Danseys Pass`} className="h-full w-full object-cover" />
          <ZoomHint />
        </div>
      </Reveal>
      <Reveal y={30} delay={0.16} className="mt-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {gallery.map((src, i) => (
            <div
              key={src}
              className={`img-frame aspect-square cursor-pointer group ${i % 3 === 1 ? "md:mt-8" : ""}`}
              onClick={() => setIndex(i + 1)}
              data-testid={`dome-gallery-${dome.id}-${i + 1}`}
            >
              <img src={src} alt={`${dome.name} dome — gallery photo ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
              <ZoomHint />
            </div>
          ))}
        </div>
      </Reveal>
      <Lightbox photos={photos} index={index} onClose={() => setIndex(null)} onNav={nav} title={dome.fullName} />
    </>
  );
}

function DomeSection({ dome }) {
  return (
    <section id={dome.id} className="py-20 md:py-28 px-6 md:px-16 scroll-mt-24" data-testid={`dome-section-${dome.id}`}>
      <div className="max-w-6xl mx-auto">
        <div className="max-w-3xl">
          <Reveal><Eyebrow className="mb-5">{dome.number} · {dome.fullName}</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="display-2 text-cream flex items-center gap-4 flex-wrap">
              {dome.name}
              {dome.id === "piwakawaka" && <FantailBird />}
              {dome.id === "karearea" && <FalconBird />}
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="serif-italic text-xl md:text-2xl text-sage mt-6 leading-snug">{dome.tagline}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-sage text-sm md:text-base leading-relaxed mt-6">{dome.blurb}</p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="flex flex-wrap gap-x-6 gap-y-2 border-y border-white/10 py-4 my-8">
              {dome.specs.map((s, i) => (
                <span key={s} className={`font-mono text-[0.66rem] uppercase tracking-[0.16em] ${i === dome.specs.length - 1 ? "text-gold" : "text-sage"}`}>
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="flex flex-wrap gap-2.5">
              {dome.amenities.map((a) => (
                <span key={a} className="chip">{a}</span>
              ))}
            </div>
          </Reveal>
          {dome.rating && (
            <Reveal delay={0.34}>
              <p className="mt-8 inline-flex items-center gap-2 text-sm text-sage" data-testid={`dome-rating-${dome.id}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#EAB308" aria-hidden="true">
                  <path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.6 7L12 17.3 5.8 20.9l1.6-7L2 9.2l7.1-.6L12 2z" />
                </svg>
                {dome.rating} · {dome.reviews} reviews on Airbnb
              </p>
            </Reveal>
          )}
          <Reveal delay={0.38}>
            <Link to="/book" className="btn-primary inline-flex mt-8" data-testid={`book-dome-${dome.id}-button`}>
              Book {dome.name}
            </Link>
          </Reveal>
        </div>

        <GalleryBlock dome={dome} />
      </div>
    </section>
  );
}

export default function Domes() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const target = document.querySelector(hash);
    if (!target) return;
    const t = setTimeout(() => {
      if (window.__lenis) window.__lenis.scrollTo(target, { offset: -80, duration: 1.2 });
      else target.scrollIntoView({ behavior: "smooth" });
    }, 350);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <section className="relative h-[72svh] min-h-[520px] overflow-hidden flex items-end" data-testid="domes-hero">
        <div className="absolute inset-0">
          <img src={IMAGES.overview} alt="The two geodesic domes on the farm" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/40" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto w-full px-6 md:px-8 pb-16">
          <Eyebrow className="mb-6">The Domes</Eyebrow>
          <LineReveal lines={["Two domes.", "One wild valley."]} className="display-1 text-cream" />
        </div>
      </section>

      <DomeSection dome={DOMES[0]} />
      <div className="border-t border-white/10" />
      <DomeSection dome={DOMES[1]} />

      <BookingSearch className="pb-2" />

      <div className="pt-20 md:pt-24" data-testid="domes-reviews-embed">
        <ReviewsWall />
      </div>

      <section className="py-20 md:py-28 px-6 md:px-16 bg-moss" data-testid="domes-gallery-section">
        <div className="max-w-6xl mx-auto">
          <Reveal><Eyebrow className="mb-5 text-center block">The seasons</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="display-2 text-cream text-center mb-12">The same valley, <span className="serif-italic text-lavglow">every mood.</span></h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { img: IMAGES.snow, alt: "Domes in the snow" },
              { img: IMAGES.hottub, alt: "Private hot tub with a view" },
              { img: IMAGES.lavender, alt: "Lavender in bloom" },
              { img: IMAGES.frost, alt: "Winter frost on the paddocks" },
            ].map((g, i) => (
              <Reveal key={g.alt} delay={i * 0.08} y={30}>
                <div className={`img-frame aspect-square ${i % 2 === 1 ? "md:mt-8" : ""}`}>
                  <img src={g.img} alt={g.alt} className="h-full w-full object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BookingCta />
    </>
  );
}
